"use client";
import { Copy, Download, X } from "lucide-react";
import { useState } from "react";
import { addMessage } from "@/components/Messages";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface ResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  content: string;
  fileName?: string;
  onCopyContent?: (content: string) => Promise<void> | void;
  onDownloadContent?: (content: string, fileName: string) => Promise<void> | void;
}

export const ResultDialog = ({
  open,
  onOpenChange,
  title,
  description,
  content,
  fileName = "result.srt",
  onCopyContent,
  onDownloadContent,
}: ResultDialogProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (onCopyContent) {
        await onCopyContent(content);
      } else {
        await navigator.clipboard.writeText(content);
      }
      setCopied(true);
      addMessage({
        text: "Copied to clipboard",
        type: "SUCCESS",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      addMessage({
        text: "Failed to copy to clipboard",
        type: "ERROR",
      });
    }
  };

  const handleDownload = async () => {
    if (onDownloadContent) {
      await onDownloadContent(content, fileName);
    } else {
      const blob = new Blob([content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    addMessage({
      text: "File downloaded",
      type: "SUCCESS",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{title}</span>
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex space-x-2">
              <Button
                onClick={handleCopy}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Copy className="h-4 w-4" />
                <span>{copied ? "Copied!" : "Copy"}</span>
              </Button>
              <Button
                onClick={handleDownload}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <Textarea
              value={content}
              readOnly
              className="h-full min-h-[300px] font-mono text-sm resize-none"
              placeholder="No content to display"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
